<template>
  <div>
    

      <div class="space-y-5">
        <!-- Header + filters -->
        <header class="flex flex-wrap items-start justify-between gap-4">
          <div class="flex items-center gap-3">
            <h1 class="text-xl font-semibold text-slate-900">General report</h1>
            <div v-if="loading" class="flex items-center gap-2">
              <div class="w-5 h-5 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
              <span class="text-xs text-slate-500">Refreshing…</span>
            </div>
          </div>
          <p class="self-center mt-1 text-xs text-slate-500">{{ rangeLabel }}</p>

          <div class="flex flex-wrap items-end gap-3">
            <!-- Date range -->
            <label class="flex flex-col gap-1 text-[11px] text-slate-500">
              Date range
              <div
                class="flex items-center gap-2 h-9 px-2 bg-white border rounded-md border-[#d9dee7] text-xs text-slate-700"
              >
                <input
                  v-model="reportFilters.from"
                  type="date"
                  :max="reportFilters.to"
                  class="w-[110px] bg-transparent outline-none"
                  aria-label="From date"
                />
                <span class="text-slate-400">–</span>
                <input
                  v-model="reportFilters.to"
                  type="date"
                  :min="reportFilters.from"
                  class="w-[110px] bg-transparent outline-none"
                  aria-label="To date"
                />
                <svg
                  class="w-4 h-4 text-[#f7941d] shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              </div>
            </label>

            <!-- Age group -->
            <label class="flex flex-col gap-1 text-[11px] text-slate-500">
              Age group
              <select
                v-model="reportFilters.ageGroup"
                class="h-9 w-[130px] px-2 bg-white border rounded-md border-[#d9dee7] text-xs text-slate-700 outline-none focus:border-[#0d3696]"
              >
                <option value="All">All ages</option>
                <option v-for="group in ageGroups" :key="group" :value="group">
                  {{ group }}
                </option>
              </select>
            </label>

            <!-- Gender -->
            <div class="flex flex-col gap-1 text-[11px] text-slate-500">
              Gender
              <div
                class="flex items-center h-9 gap-1 p-1 bg-white border rounded-md border-[#d9dee7]"
              >
                <button
                  v-for="option in genderFilters"
                  :key="option"
                  type="button"
                  class="h-full px-3 text-xs transition-colors rounded"
                  :class="
                    reportFilters.gender === option
                      ? 'bg-[#0d3696] font-medium text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  "
                  @click="reportFilters.gender = option"
                >
                  {{ option }}
                </button>
              </div>
            </div>
          </div>
        </header>

        <div
          v-if="error"
          class="px-4 py-2 text-xs border rounded-lg border-amber-200 bg-amber-50 text-amber-700"
        >
          Couldn't refresh the numbers just now — change a filter to try again.
        </div>

        <!-- Summary cards -->
        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div
            class="flex items-start justify-between p-5 text-white rounded-xl bg-[#0d3696]"
          >
            <div>
              <p class="text-xs text-white/80">Blood Units in supply</p>
              <p class="mt-2 text-3xl font-bold tracking-tight">
                {{ formatNumber(supplyTotal) }}
              </p>
              <p class="mt-3 text-[11px] text-white/70">All donors, all ages</p>
            </div>
            <div
              class="flex items-center justify-center w-8 h-8 rounded-md"
            >
              <svg
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
                <path d="M3.3 7L12 12l8.7-5M12 22V12" />
              </svg>
            </div>
          </div>

          <div
            class="flex items-start justify-between p-5 rounded-xl bg-[#f7941d] text-[#0d3696]"
          >
            <div>
              <p class="text-xs opacity-90">Blood Units Demanded</p>
              <p class="mt-2 text-3xl font-bold tracking-tight">
                {{ formatNumber(demandTotal) }}
              </p>
              <p class="mt-3 text-[11px] opacity-80">Total requisitions</p>
            </div>
            <div
              class="flex items-center justify-center w-8 h-8 rounded-md"
            >
              <svg
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="8" y="2" width="8" height="4" rx="1" />
                <path
                  d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2M9 12h6M9 16h6"
                />
              </svg>
            </div>
          </div>

          <div
            class="flex items-start justify-between p-5 border rounded-xl border-[#c9efd6] bg-[#e8f8ee]"
          >
            <div>
              <p class="text-xs text-slate-600">Demand coverage</p>
              <p class="mt-2 text-3xl font-bold tracking-tight text-[#029682]">
                {{ coverage }}%
              </p>
              <p class="mt-3 text-[11px] text-[#029682]">
                {{
                  coverage >= 100
                    ? "Supply meets demand"
                    : "Supply below demand"
                }}
              </p>
            </div>
            <div
              class="flex items-center justify-center w-8 h-8 rounded-md text-[#029682]"
            >
              <svg
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 14l4-4" />
                <path d="M3.3 19a10 10 0 1117.4 0" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Charts -->
        <div class="grid gap-4 p-4 rounded-xl lg:grid-cols-3 bg-[#f1f4f9]">
          <!-- Supply vs demand -->
          <section class="relative p-5 bg-white rounded-xl lg:col-span-2">
            <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
              <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
            </div>
            <header
              class="flex flex-wrap items-start justify-between gap-3 mb-4"
            >
              <div>
                <h2 class="text-sm font-semibold text-slate-900">
                  Supply vs demand by blood type
                </h2>
                <p class="mt-0.5 text-[11px] text-slate-500">
                  Select a bar to filter the table below
                </p>
              </div>
              <div class="flex items-center gap-3 text-[11px] text-slate-600">
                <span class="flex items-center gap-1.5">
                  <span class="w-2 h-2 rounded-sm bg-[#0d3696]"></span> Supply
                </span>
                <span class="flex items-center gap-1.5">
                  <span class="w-2 h-2 rounded-sm bg-[#f7941d]"></span> Demand
                </span>
              </div>
            </header>

            <div class="flex">
              <!-- Y axis -->
              <div class="relative w-11 h-[260px] shrink-0">
                <span
                  v-for="tick in yTicks"
                  :key="tick"
                  class="absolute right-2 text-[10px] text-slate-500 translate-y-1/2"
                  :style="{ bottom: (tick / chartMax) * 100 + '%' }"
                >
                  {{ formatNumber(tick) }}
                </span>
              </div>

              <!-- Plot -->
              <div class="flex-1 min-w-0">
                <div class="relative h-[260px]">
                  <div
                    v-for="tick in yTicks"
                    :key="'grid-' + tick"
                    class="absolute left-0 right-0 border-t border-[#eef1f5]"
                    :style="{ bottom: (tick / chartMax) * 100 + '%' }"
                  ></div>

                  <div class="relative flex h-full">
                    <button
                      v-for="row in byType"
                      :key="row.type"
                      type="button"
                      class="relative flex items-end justify-center flex-1 h-full gap-1 transition-opacity focus:outline-none"
                      :class="
                        tableBloodType !== 'All' && tableBloodType !== row.type
                          ? 'opacity-40 hover:opacity-70'
                          : 'opacity-100'
                      "
                      :aria-label="`Filter table by ${row.type}`"
                      :aria-pressed="tableBloodType === row.type"
                      @click="toggleBloodType(row.type)"
                      @mouseenter="hoveredBloodType = row.type"
                      @mouseleave="hoveredBloodType = null"
                    >
                      <div
                        class="w-[32%] max-w-[16px] rounded-t-[2px] bg-[#0d3696]"
                        :style="{ height: (row.supply / chartMax) * 100 + '%' }"
                        :title="supplyTooltip(row)"
                      ></div>
                      <div
                        class="w-[32%] max-w-[16px] rounded-t-[2px] bg-[#f7941d]"
                        :style="{ height: (row.demand / chartMax) * 100 + '%' }"
                        :title="demandTooltip(row)"
                      ></div>
                    </button>
                    <div
                      v-if="hoveredBloodType"
                      class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-[10px] rounded shadow-lg whitespace-nowrap z-10"
                      style="min-width: 320px"
                    >
                      <div
                        class="pb-1 mb-2 font-semibold text-center border-b border-slate-700"
                      >
                        {{ hoveredBloodType }}
                      </div>
                      <table class="w-full text-left border-collapse">
                        <thead>
                          <tr class="border-b border-slate-700">
                            <th class="px-2 py-1 text-xs opacity-70">Product</th>
                            <th class="px-2 py-1 text-xs opacity-70 text-[#02b9a1] font-medium">Supply</th>
                            <th class="px-2 py-1 text-xs opacity-70 text-[#f7941d] font-medium">Demand</th>
                            <th class="px-2 py-1 text-xs opacity-70 text-[#f7941d] font-medium">Coverage</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="product in getAllProductsForType(hoveredBloodType)"
                            :key="product"
                            class="border-b border-slate-800"
                          >
                            <td class="px-2 py-1 truncate max-w-[80px]">{{ productLabel(product) }}</td>
                            <td class="px-2 py-1 text-[#02b9a1]">{{ formatNumber(getBloodTypeData(hoveredBloodType).supplyByProduct[product] || 0) }}</td>
                            <td class="px-2 py-1 text-[#f7941d]">{{ formatNumber(getBloodTypeData(hoveredBloodType).demandByProduct[product] || 0) }}</td>
                            <td class="px-2 py-1" :class="getCoverageClass(hoveredBloodType, product)">{{ getCoverage(hoveredBloodType, product) }}%</td>
                          </tr>
                          <tr class="font-medium border-t border-slate-700">
                            <td class="px-2 py-1">Total</td>
                            <td class="px-2 py-1 text-[#02b9a1]">{{ formatNumber(getBloodTypeData(hoveredBloodType).supply) }}</td>
                            <td class="px-2 py-1 text-[#f7941d]">{{ formatNumber(getBloodTypeData(hoveredBloodType).demand) }}</td>
                            <td class="px-2 py-1" :class="getCoverageClass(hoveredBloodType, 'total')">{{ getBloodTypeData(hoveredBloodType).coverage }}%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div class="flex mt-2 border-t border-[#e5e7eb] pt-2">
                  <span
                    v-for="row in byType"
                    :key="'label-' + row.type"
                    class="flex-1 text-[11px] text-center text-slate-600"
                  >
                    {{ row.type }}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <div class="grid gap-4 lg:col-span-1">
            <!-- Gender -->
            <section class="relative p-5 bg-white rounded-xl">
              <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
                <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
              </div>
              <h2 class="mb-3 text-sm font-semibold text-slate-900">
                Supply by donor gender
              </h2>

              <div
                class="flex w-full h-2 overflow-hidden rounded-full bg-slate-100"
              >
                <div
                  class="h-full bg-[#0d3696]"
                  :style="{
                    width: percentOf(genderUnits('Male'), genderTotal) + '%',
                  }"
                ></div>
                <div
                  class="h-full bg-[#8ea3de]"
                  :style="{
                    width: percentOf(genderUnits('Female'), genderTotal) + '%',
                  }"
                ></div>
              </div>

              <ul class="mt-4 space-y-2">
                <li
                  v-for="row in gender"
                  :key="row.name"
                  class="flex items-center justify-between text-xs"
                >
                  <span class="flex items-center gap-2 text-slate-700">
                    <span
                      class="w-2 h-2 rounded-full"
                      :class="
                        row.name === 'Male'
                          ? 'bg-[#0d3696]'
                          : row.name === 'Female'
                          ? 'bg-[#8ea3de]'
                          : 'bg-slate-400'
                      "
                    ></span>
                    <span class="font-medium">{{ row.name }}</span>
                  </span>
                  <span class="flex items-center gap-4">
                    <span class="text-slate-800">{{
                      formatNumber(row.units)
                    }}</span>
                    <span class="w-8 text-right text-slate-500"
                      >{{ percentOf(row.units, genderTotal) }}%</span
                    >
                  </span>
                </li>
              </ul>
            </section>

            <!-- Age -->
            <section class="relative p-5 bg-white rounded-xl">
              <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
                <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
              </div>
              <h2 class="mb-4 text-sm font-semibold text-slate-900">
                Supply by donor age
              </h2>

              <div class="flex">
                <div class="relative w-10 h-[130px] shrink-0">
                  <span
                    v-for="tick in ageTicks"
                    :key="tick"
                    class="absolute right-2 text-[9px] text-slate-500 translate-y-1/2"
                    :style="{ bottom: (tick / ageChartMax) * 100 + '%' }"
                  >
                    {{ formatNumber(tick) }}
                  </span>
                </div>

                <div class="flex-1 min-w-0">
                  <div
                    class="flex items-end justify-around h-[130px] gap-2 border-b border-[#e5e7eb]"
                  >
                    <div
                      v-for="row in age"
                      :key="row.name"
                      class="flex items-end justify-center flex-1 h-full"
                    >
                      <div
                        class="w-full max-w-[22px] rounded-t-[2px] bg-[#0d3696]"
                        :style="{
                          height: (row.units / ageChartMax) * 100 + '%',
                        }"
                        :title="`${row.name}: ${formatNumber(row.units)}`"
                      ></div>
                    </div>
                  </div>
                  <div class="flex gap-2 mt-2">
                    <span
                      v-for="row in age"
                      :key="'age-' + row.name"
                      class="flex-1 text-[9px] text-center text-slate-500"
                    >
                      {{ row.name }}
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        <!-- Inventory table -->
        <section
          class="overflow-hidden bg-white border rounded-xl border-[#e5e7eb] relative"
        >
          <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
            <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
          </div>
          <div class="p-5">
            <header
              class="flex flex-wrap items-center justify-between gap-3 mb-4"
            >
              <h2 class="text-sm font-semibold text-slate-900">
                Inventory by product
              </h2>
              <div class="relative">
                <svg
                  class="absolute w-3.5 h-3.5 -translate-y-1/2 left-3 top-1/2 text-slate-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.3-4.3" />
                </svg>
                <input
                  v-model="localSearchQuery"
                  placeholder="Search product..."
                  class="w-full h-9 pl-8 pr-3 text-xs bg-white border rounded-md outline-none border-[#d9dee7] text-slate-700 placeholder:text-slate-400 focus:border-[#0d3696] sm:w-[220px]"
                />
              </div>
            </header>

            <div class="flex flex-wrap gap-2">
              <button
                v-for="type in ['All', ...bloodTypes]"
                :key="type"
                type="button"
                class="px-3 py-1 text-xs transition-colors border rounded-full"
                :class="
                  tableBloodType === type
                    ? 'border-[#0d3696] bg-[#0d3696] font-medium text-white'
                    : 'border-[#d9dee7] bg-white text-slate-700 hover:bg-slate-50'
                "
                @click="tableBloodType = type"
              >
                {{ type === "All" ? "All types" : type }}
              </button>
            </div>
          </div>

          <div
            class="overflow-x-auto transition-opacity"
            :class="{ 'opacity-60': loading }"
          >
            <table class="w-full min-w-[620px] text-sm border-collapse">
              <thead>
                <tr
                  class="border-y border-[#e5e7eb] text-left text-[11px] text-slate-500"
                >
                  <th class="px-5 py-3 font-normal">Blood type</th>
                  <th class="px-3 py-3 font-normal">Product</th>
                  <th class="px-3 py-3 font-normal text-right">Supply</th>
                  <th class="px-3 py-3 font-normal text-right">Demand</th>
                  <th class="px-5 py-3 font-normal text-right">
                    Target deficit
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in visibleRows"
                  :key="row.key"
                  class="border-b border-[#f1f5f9]"
                >
                  <td class="px-5 py-3">
                    <span
                      class="inline-block px-2 py-0.5 text-[11px] font-medium text-teal-600 bg-teal-100 rounded"
                    >
                      {{ row.blood_type }}
                    </span>
                  </td>
                  <td class="px-3 py-3">
                    <span class="text-xs font-semibold text-slate-800">{{
                      row.product
                    }}</span>
                    <span class="ml-1.5 text-[10px] text-slate-500">{{
                      productLabel(row.product)
                    }}</span>
                  </td>
                  <td class="px-3 py-3 text-xs text-right text-slate-800">
                    {{ formatNumber(row.supply) }}
                  </td>
                  <td class="px-3 py-3 text-xs text-right">
                    <span
                      class="inline-block px-2 py-0.5 rounded"
                      :class="
                        row.demand > row.supply
                          ? 'bg-rose-100 font-medium text-rose-600'
                          : 'text-slate-800'
                      "
                    >
                      {{ formatNumber(row.demand) }}
                    </span>
                  </td>
                  <td
                    class="px-5 py-3 text-xs text-right"
                    :class="
                      row.deficit > 0
                        ? 'font-bold text-rose-600'
                        : 'font-medium text-emerald-600'
                    "
                    :title="`Demand: ${formatNumber(row.demand)} | Supply: ${formatNumber(row.supply)}`"
                  >
                    {{ formatNumber(row.deficit) }}
                  </td>
                </tr>
                <tr v-if="!visibleRows.length">
                  <td
                    colspan="5"
                    class="py-10 text-sm text-center text-slate-400"
                  >
                    No records match these filters.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            class="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-[#e5e7eb] bg-[#f8fafc]"
          >
            <p class="text-[11px] text-slate-500">
              Showing {{ visibleRows.length }} of
              {{ filteredRows.length }} records
            </p>
            <div v-if="totalPages > 1" class="flex items-center gap-2">
              <button
                type="button"
                class="flex items-center justify-center w-7 h-7 bg-white border rounded-md border-[#e5e7eb] text-slate-500 disabled:opacity-40"
                :disabled="currentPage === 1"
                aria-label="Previous page"
                @click="currentPage--"
              >
                <svg
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <span class="text-[11px] text-slate-500"
                >Page {{ currentPage }} of {{ totalPages }}</span
              >
              <button
                type="button"
                class="flex items-center justify-center w-7 h-7 bg-white border rounded-md border-[#e5e7eb] text-slate-500 disabled:opacity-40"
                :disabled="currentPage === totalPages"
                aria-label="Next page"
                @click="currentPage++"
              >
                <svg
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        </section>
      </div>

  </div>
</template>

<script>
import api from "@/services/api";

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const PRODUCTS = [
  { code: "WB", label: "Whole blood" },
  { code: "FFP", label: "Fresh frozen plasma" },
  { code: "PRC", label: "Packed red cells" },
  { code: "AP", label: "Apheresis platelets" },
  { code: "PLTS", label: "Platelets" },
  { code: "IPRC", label: "Irradiated red cells" },
];

const AGE_GROUPS = ["18-24", "25-34", "35-44", "45-54", "55-65"];
const GENDERS = ["Male", "Female", "Other"];
const GENDER_FILTERS = ["All", "Male", "Female", "Other"];

const toISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseISODate = (value) => new Date(`${value}T00:00:00`);

const shortDate = (value, withYear = true) =>
  parseISODate(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
  });

export default {
  name: "BloodDonationsReportTab",
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
    filters: {
      type: Object,
      default: () => ({
        blood_group: "",
        status: "",
        start_date: "",
        end_date: "",
        donation_type: "",
        technology: "",
      }),
    },
  },
  data() {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 29);

    return {
      loading: false,
      error: null,
      user: null,
      facility_logo: "",
      localSearchQuery: "",

      // constants exposed to the template
      bloodTypes: BLOOD_TYPES,
      ageGroups: AGE_GROUPS,
      genderFilters: GENDER_FILTERS,

      // report filters (the new design's own filters)
      reportFilters: {
        from: toISODate(thirtyDaysAgo),
        to: toISODate(today),
        ageGroup: "All",
        gender: "All",
      },

      // raw report data
      supplyMap: {},
      demandMap: {},
      targetMap: {},
      segments: [],

      // table state
      tableBloodType: "All",
      currentPage: 1,
      perPage: 10,

      debounceTimer: null,

      // Chart hover state
      hoveredBloodType: null,
    };
  },
  created() {
    this.localSearchQuery = this.searchQuery;
    this.user = this.$store.getters["auth/user"];
    this.facility_logo =
      this.user?.user_roles?.[0]?.facility_department?.facility?.logo;

    // plain (non-reactive) caches
    this.summaryCache = new Map();
    this.pendingRequests = new Map();

    this.loadReport();
  },
  beforeUnmount() {
    clearTimeout(this.debounceTimer);
  },
  computed: {
    /* ----------------------------- totals ----------------------------- */
    supplyTotal() {
      return this.sumValues(this.supplyMap);
    },
    demandTotal() {
      return this.sumValues(this.demandMap);
    },
    coverage() {
      return this.demandTotal
        ? Math.round((this.supplyTotal / this.demandTotal) * 100)
        : 0;
    },

    /* ------------------------------ labels ---------------------------- */
    rangeLabel() {
      const { from, to, ageGroup, gender } = this.reportFilters;
      if (!from || !to) return "";
      const days =
        Math.round((parseISODate(to) - parseISODate(from)) / 86400000) + 1;
      const range =
        days === 30
          ? "Last 30 days"
          : `${shortDate(from, false)} – ${shortDate(to)}`;
      const ages = ageGroup === "All" ? "All ages" : `Ages ${ageGroup}`;
      const genders = gender === "All" ? "All genders" : gender;
      return `${range} · ${ages} · ${genders}`;
    },

    /* ------------------------------ charts ---------------------------- */
    byType() {
      return BLOOD_TYPES.map((type) => {
        const supplyByProduct = {};
        const demandByProduct = {};
        let totalSupply = 0;
        let totalDemand = 0;

        Object.entries(this.supplyMap).forEach(([key, units]) => {
          if (key.startsWith(`${type}|`)) {
            const product = key.split("|")[1];
            supplyByProduct[product] = (supplyByProduct[product] || 0) + units;
            totalSupply += units;
          }
        });

        Object.entries(this.demandMap).forEach(([key, units]) => {
          if (key.startsWith(`${type}|`)) {
            const product = key.split("|")[1];
            demandByProduct[product] = (demandByProduct[product] || 0) + units;
            totalDemand += units;
          }
        });

        return {
          type,
          supply: totalSupply,
          demand: totalDemand,
          supplyByProduct,
          demandByProduct,
          coverage: totalDemand
            ? Math.round((totalSupply / totalDemand) * 100)
            : 0,
        };
      });
    },
    chartStep() {
      return this.niceStep(
        Math.max(0, ...this.byType.map((r) => Math.max(r.supply, r.demand))),
      );
    },
    chartMax() {
      return this.chartStep * 4;
    },
    yTicks() {
      return [0, 1, 2, 3, 4].map((i) => i * this.chartStep);
    },
    gender() {
      return GENDERS.map((name) => ({
        name,
        units: this.segments
          .filter((s) => s.gender === name)
          .reduce((sum, s) => sum + (s.units || 0), 0),
      }));
    },
    genderTotal() {
      return this.gender.reduce((sum, r) => sum + r.units, 0);
    },
    genderMax() {
      return Math.max(1, ...this.gender.map((r) => r.units));
    },
    age() {
      return AGE_GROUPS.map((name) => ({
        name,
        units: this.segments
          .filter((s) => s.age_group === name)
          .reduce((sum, s) => sum + (s.units || 0), 0),
      }));
    },
    ageStep() {
      return this.niceStep(Math.max(0, ...this.age.map((r) => r.units)));
    },
    ageChartMax() {
      return this.ageStep * 4;
    },
    ageTicks() {
      return [0, 1, 2, 3, 4].map((i) => i * this.ageStep);
    },

    /* ------------------------------ table ----------------------------- */
    tableRows() {
      const keys = Array.from(
        new Set([
          ...Object.keys(this.supplyMap),
          ...Object.keys(this.demandMap),
        ]),
      );
      return keys
        .map((key) => {
          const [blood_type, product] = key.split("|");
          const supply = this.supplyMap[key] || 0;
          const demand = this.demandMap[key] || 0;
          // Deficit: 0 if supply meets/exceeds demand, else demand - supply
          const deficit = supply >= demand ? 0 : demand - supply;
          return {
            key,
            blood_type,
            product,
            supply,
            demand,
            deficit,
          };
        })
        .sort(
          (a, b) =>
            this.bloodTypeOrder(a.blood_type) -
              this.bloodTypeOrder(b.blood_type) ||
            this.productOrder(a.product) - this.productOrder(b.product),
        );
    },
    filteredRows() {
      const term = (this.localSearchQuery || "").trim().toLowerCase();
      return this.tableRows.filter((row) => {
        const matchType =
          this.tableBloodType === "All" ||
          row.blood_type === this.tableBloodType;
        const matchSearch =
          !term ||
          `${row.blood_type} ${row.product}`.toLowerCase().includes(term);
        return matchType && matchSearch;
      });
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.perPage));
    },
    visibleRows() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredRows.slice(start, start + this.perPage);
    },
  },
  watch: {
    searchQuery(newVal) {
      this.localSearchQuery = newVal;
    },
    localSearchQuery(newVal) {
      this.$emit("update:searchQuery", newVal);
      this.currentPage = 1;
    },
    tableBloodType() {
      this.currentPage = 1;
    },
    reportFilters: {
      handler() {
        // debounce so quick filter changes make one request
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.loadReport(), 300);
      },
      deep: true,
    },
  },
  methods: {
    /* ----------------------------- helpers ---------------------------- */
    // round max/4 up to a "nice" step so axis ticks are 0, s, 2s, 3s, 4s
    niceStep(max) {
      if (max <= 0) return 5;
      const rough = max / 4;
      const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
      const normalized = rough / magnitude;
      const nice = [1, 2, 2.5, 5, 10].find((n) => n >= normalized) || 10;
      return nice * magnitude;
    },
    genderUnits(name) {
      return (this.gender.find((g) => g.name === name) || {}).units || 0;
    },
    // clicking a bar group filters the table; clicking it again clears the filter
    toggleBloodType(type) {
      this.tableBloodType = this.tableBloodType === type ? "All" : type;
    },
    formatNumber(value) {
      return (value ?? 0).toLocaleString("en-US");
    },
    percentOf(value, total) {
      return total ? Math.round((value / total) * 100) : 0;
    },
    productLabel(code) {
      return PRODUCTS.find((p) => p.code === code)?.label || code;
    },
    productOrder(code) {
      const i = PRODUCTS.findIndex((p) => p.code === code);
      return i === -1 ? PRODUCTS.length : i;
    },
    bloodTypeOrder(value) {
      const i = BLOOD_TYPES.indexOf(value);
      return i === -1 ? BLOOD_TYPES.length : i;
    },
    sumValues(map) {
      return Object.values(map).reduce((sum, units) => sum + units, 0);
    },
    typeUnits(map, type) {
      return Object.entries(map)
        .filter(([key]) => key.startsWith(`${type}|`))
        .reduce((sum, [, units]) => sum + units, 0);
    },
    getBloodTypeData(type) {
      return (
        this.byType.find((r) => r.type === type) || {
          supplyByProduct: {},
          demandByProduct: {},
        }
      );
    },
    getAllProductsForType(type) {
      const data = this.getBloodTypeData(type);
      const supplyProducts = Object.keys(data.supplyByProduct || {});
      const demandProducts = Object.keys(data.demandByProduct || {});
      return [...new Set([...supplyProducts, ...demandProducts])].sort();
    },
    getCoverage(type, product) {
      const data = this.getBloodTypeData(type);
      if (product === "total") return data.coverage || 0;
      const supply = data.supplyByProduct[product] || 0;
      const demand = data.demandByProduct[product] || 0;
      return demand ? Math.round((supply / demand) * 100) : 0;
    },
    getCoverageClass(type, product) {
      const coverage = this.getCoverage(type, product);
      if (coverage >= 100) return "text-teal-400";
      if (coverage >= 50) return "text-yellow-400";
      return "text-red-400";
    },
    supplyTooltip(row) {
      const lines = [`${row.type} supply: ${this.formatNumber(row.supply)}`];
      Object.entries(row.supplyByProduct).forEach(([product, units]) => {
        lines.push(
          `  ${this.productLabel(product)}: ${this.formatNumber(units)}`,
        );
      });
      return lines.join("\n");
    },
    demandTooltip(row) {
      const lines = [`${row.type} demand: ${this.formatNumber(row.demand)}`];
      Object.entries(row.demandByProduct).forEach(([product, units]) => {
        lines.push(
          `  ${this.productLabel(product)}: ${this.formatNumber(units)}`,
        );
      });
      return lines.join("\n");
    },
    formatDate(date) {
      if (!date) return "N/A";
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        console.error("Invalid date:", date);
        return "N/A";
      }
      return parsedDate.toLocaleString();
    },

    /* ------------------------------- data ----------------------------- */

    /**
     * Expected API response (adjust to match your backend):
     * {
     *   supply:   [{ blood_type: "A+", product: "PRC", units: 120 }],
     *   demand:   [{ blood_type: "A+", product: "PRC", units: 150 }],
     *   targets:  [{ blood_type: "A+", product: "PRC", target_units: 200 }],
     *   segments: [{ gender: "Male", age_group: "25-34", units: 340 }]
     * }
     */
    normalizeReport(response) {
      const toMap = (rows, field = "units") =>
        (rows || []).reduce((map, row) => {
          const key = `${row.blood_type}|${row.product}`;
          map[key] = (map[key] || 0) + (row[field] || 0);
          return map;
        }, {});

      return {
        supplyMap: toMap(response.supply),
        demandMap: toMap(response.demand),
        targetMap: toMap(response.targets, "target_units"),
        segments: response.segments || [],
      };
    },
    fetchReport(key, params) {
      if (this.summaryCache.has(key)) {
        return Promise.resolve(this.summaryCache.get(key));
      }
      if (this.pendingRequests.has(key)) {
        return this.pendingRequests.get(key);
      }

      const source = api.getBloodDemandReport(params);

      const request = source
        .then((response) => {
          const value = this.normalizeReport(response.data || response);
          this.summaryCache.set(key, value);
          this.pendingRequests.delete(key);
          return value;
        })
        .catch((err) => {
          this.pendingRequests.delete(key);
          throw err;
        });

      this.pendingRequests.set(key, request);
      return request;
    },
    async loadReport() {
      const { from, to, ageGroup, gender } = this.reportFilters;
      if (!from || !to) return;

      const params = { start_date: from, end_date: to };
      if (ageGroup !== "All") params.age_group = ageGroup;
      if (gender !== "All") params.gender = gender;

      const key = [from, to, ageGroup, gender].join("~");
      this.loading = true;
      this.error = null;

      try {
        const value = await this.fetchReport(key, params);
        // ignore stale responses if filters changed while loading
        const current = [
          this.reportFilters.from,
          this.reportFilters.to,
          this.reportFilters.ageGroup,
          this.reportFilters.gender,
        ].join("~");
        if (current !== key) return;

        this.supplyMap = value.supplyMap;
        this.demandMap = value.demandMap;
        this.targetMap = value.targetMap;
        this.segments = value.segments;
        this.currentPage = 1;
      } catch (error) {
        this.error = error.message || "Failed to fetch report";
        console.error("Error fetching demand report:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="css" scoped></style>
